import {
  ActionGroup,
  isAction,
  isActionGroup,
  isRouteItem,
  isSelectableItem,
  resolveActionGroupPreferences,
  SelectableItem,
  useFindSelectedItem,
} from "shared-ts-ui-action";
import {
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
} from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { NavigationBarMenu } from "./NavigationBarMenu";
import { NavigationComponentProps } from "../NavigationComponentProps";
import { useSxMerge } from "shared-ts-mui";
import { Layout } from "../Layout";
import { useResolveVisibleAndOverflowItems } from "./useResolveVisibleAndOverflowItems";

const layout: Layout = "compact";

export const NavigationBar = (props: NavigationComponentProps) => {
  const { items, onSelected } = props;
  const variant = props.variant ?? "elevation";
  const theme = useTheme();
  const selectedItem = useFindSelectedItem(props.items);
  const value = selectedItem?.reference ?? undefined;
  const { visibleItems, overflowedItems, ref } =
    useResolveVisibleAndOverflowItems(items);

  const overflowGroup = useMemo(() => {
    return {
      type: "ActionGroup",
      items: overflowedItems,
    } as ActionGroup;
  }, [overflowedItems]);

  const handleItemSelected = useCallback(
    (selectedItem: SelectableItem) => {
      onSelected?.(selectedItem);
    },
    [onSelected]
  );

  const sx = useSxMerge(props.sx, {
    borderTop: "1px solid " + theme.palette.divider,
    justifyContent: "space-around",
  });

  return (
    <BottomNavigation
      className={
        `NavigationBar ${variant}` +
        (props.className !== null ? ` ${props.className}` : "")
      }
      classes={props.classes}
      showLabels
      value={value}
      sx={sx}
      ref={ref}
    >
      {visibleItems.map((item, index) => {
        if (item === undefined) {
          return undefined;
        } else if (isAction(item)) {
          const selected = isSelectableItem(item) && item.selected;
          const handleItemClick = () => {
            item.onExecute(item);
          };

          return (
            <BottomNavigationAction
              key={index}
              label={item.name}
              value={item.reference}
              icon={item.icon}
              onClick={handleItemClick}
              sx={{
                color: selected ? theme.palette.secondary.main : undefined,
                flexGrow: 0,
              }}
            />
          );
        } else if (isRouteItem(item)) {
          const selected = isSelectableItem(item) && item.selected;
          const handleItemClick = () => {
            if (isSelectableItem(item)) {
              onSelected?.(item);
            }
          };

          return (
            <BottomNavigationAction
              key={index}
              label={item.name}
              value={item.reference}
              icon={item.icon}
              onClick={handleItemClick}
              sx={{
                color: selected ? theme.palette.secondary.main : undefined,
                flexGrow: 0,
              }}
            />
          );
        } else if (isActionGroup(item)) {
          const preferences = resolveActionGroupPreferences(
            item.preferences,
            layout
          );
          return (
            <NavigationBarMenu
              key={index}
              item={item}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              onSelected={handleItemSelected}
            />
          );
        } else {
          throw new Error("Unsupported item type: \n" + item.type);
        }
      })}
      {overflowedItems.length > 0 && (
        <NavigationBarMenu
          key={-1}
          item={overflowGroup}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          onSelected={handleItemSelected}
        />
      )}
    </BottomNavigation>
  );
};
